import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import { globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import CustomError from "../../../../utils/customError";
import { subscriptionPlanType, subscriptionPlanCategory, SWAL_SETTINGS, DYNAMIC_VARIABLES } from "../../../../utils/Constants";
import { SubscriptionPlanDetails, SubscriptionPlanEdit } from "../../services/subscriptionPlan.services";
import Loader from "../../common/loader";

const EditSubscriptionPlan = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const [submitted, setSubmitted] = useState(false);
  const [showDefault, setShowDefault] = useState(false);
  const [loader, setLoader] = useState(false);

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    {
      title: t("Subscription Plan"),
      url: "/admin/subscription/subscription-plan/list/1",
    },
    { title: t("link_add"), url: "" },
  ];

  const validationSchema = Yup.object().shape({
    plan_type: Yup.string().required("Plan Type is required"),
    plan_category: Yup.string().required("Plan Category is required"),
    name: Yup.string().trim().required("Plan name is required"),
    price_inr: Yup.number()
      .typeError("Price must be a number")
      .required("Price is required")
      .min(0, "Price cannot be less than 0"),
    duration_months: Yup.number().required("Duration is required").min(1),
  });

  useEffect(() => {
    setLoader(true);
    SubscriptionPlanDetails(params.id)
      .then((response) => {
        setShowDefault(response && response.data ? response.data : []);
        setTimeout(() => {
          setLoader(false)
        }, 300)
      })
      .catch((error) => {
        console.log("error=====>", error);
        setTimeout(() => {
          setLoader(false)
        }, 200)
      });
  }, [params.id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      plan_type: showDefault?.plan_type ? showDefault?.plan_type : "",
      plan_category: showDefault?.plan_category ? showDefault?.plan_category : "",
      name: showDefault?.name ? showDefault?.name : "",
      price_inr: showDefault?.price_inr ? showDefault?.price_inr : "",
      price_usd: showDefault?.price_usd ? showDefault?.price_usd : "",
      duration_months: showDefault?.duration_months ? showDefault?.duration_months : 1,
      android_product_id: showDefault?.android_product_id ? showDefault?.android_product_id : "",
      ios_product_id: showDefault?.ios_product_id ? showDefault?.ios_product_id : "",
      features: showDefault?.features && Array.isArray(showDefault.features) && showDefault.features.length > 0 ? showDefault.features : [""],
      icon: null
    },
    validateOnBlur: false,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      globalLoader(true);
      setSubmitted(true);
      let formData = new FormData();
      formData.append("o_id", params?.id);
      formData.append("plan_type", values.plan_type);
      formData.append("plan_category", values.plan_category);
      formData.append("name", values.name);
      formData.append("price_inr", values.price_inr);
      formData.append("price_usd", values.price_usd || 0);
      formData.append("duration_months", values.duration_months);
      formData.append("android_product_id", values.android_product_id);
      formData.append("ios_product_id", values.ios_product_id);
      formData.append("features", JSON.stringify(values.features.filter(f => f.trim() !== "")));
      if (values.icon) {
        formData.append("icon", values.icon);
      }

      SubscriptionPlanEdit(formData)
        .then((response) => {
          setSubmitting(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            resetForm({ values: "" });
            navigate(`/admin/subscription/subscription-plan/list/1`);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
            globalLoader(false);
            setSubmitted(false);
          }
        })
        .catch((error) => {
          console.log("error ====> ", error);
        });
    },
  });

  console.log("value-->", formik.values)

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <div>
                  <h6 className="main-content-label mb-3">
                    {t("Edit Subscription Plan")}
                  </h6>
                </div>
                {loader ? <Loader /> :
                  <div className="row row-sm">
                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="plan_type" className="text-left d-flex">
                        {t("Plan Type")}:
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <select
                        name="plan_type"
                        id="plan_type"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.plan_type}
                        className="form-control text-capitalize"
                      >
                        <option value="">{t("Select Type")}</option>
                        {subscriptionPlanType.map((item, index) => (
                          <option key={index} value={item.value}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                      <span className="text-danger d-flex text-left">
                        <CustomError name="plan_type" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="plan_category" className="text-left d-flex">
                        {t("Plan Category")}:
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <select
                        name="plan_category"
                        id="plan_category"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.plan_category}
                        className="form-control text-capitalize"
                      >
                        <option value="">{t("Select Category")}</option>
                        {subscriptionPlanCategory.map((item, index) => (
                          <option key={index} value={item.value}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                      <span className="text-danger d-flex text-left">
                        <CustomError name="plan_category" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="name" className="text-left d-flex">
                        {t("Plan name")}:
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <input
                        name="name"
                        id="name"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                        className="form-control"
                        placeholder={t("Enter plan name")}
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="name" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="price_inr" className="text-left d-flex">
                        {t("Plan price (INR)")}:
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <input
                        name="price_inr"
                        id="price_inr"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.price_inr}
                        className="form-control"
                        placeholder={t("Enter plan price")}
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="price_inr" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="price_usd" className="text-left d-flex">
                        {t("Plan price (USD)")}:
                      </label>
                      <input
                        name="price_usd"
                        id="price_usd"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.price_usd}
                        className="form-control"
                        placeholder={t("Enter plan price in USD")}
                      />
                    </div>
                    
                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="duration_months" className="text-left d-flex">
                        {t("Duration (Months)")}:
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <input
                        name="duration_months"
                        id="duration_months"
                        type="number"
                        min="1"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.duration_months}
                        className="form-control"
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="duration_months" form={formik} />
                      </span>
                    </div>

                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="android_product_id" className="text-left d-flex">
                        {t("Android Product ID")}:
                      </label>
                      <input
                        name="android_product_id"
                        id="android_product_id"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.android_product_id}
                        className="form-control"
                        placeholder={t("In-App Purchase Product ID")}
                      />
                    </div>

                    <div className="col-md-6 text-center form-group">
                      <label htmlFor="ios_product_id" className="text-left d-flex">
                        {t("iOS Product ID")}:
                      </label>
                      <input
                        name="ios_product_id"
                        id="ios_product_id"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.ios_product_id}
                        className="form-control"
                        placeholder={t("In-App Purchase Product ID")}
                      />
                    </div>

                    <div className="col-md-12 text-center form-group">
                      <label htmlFor="icon" className="text-left d-flex">
                        {t("Plan Icon")} (Leave blank to keep current):
                      </label>
                      <input
                        name="icon"
                        id="icon"
                        type="file"
                        accept="image/*"
                        onChange={(event) => {
                          formik.setFieldValue("icon", event.currentTarget.files[0]);
                        }}
                        className="form-control"
                      />
                      {showDefault?.icon && (
                        <div className="text-left mt-2">
                          <img src={`${DYNAMIC_VARIABLES.SERVER_URL}/public/uploads/subscription/${showDefault.icon}`} alt="Current Icon" style={{ width: '50px', height: '50px' }} />
                        </div>
                      )}
                    </div>

                    {/* <div className="col-md-12 text-left form-group">
                      <label className="text-left d-flex">
                        {t("Features Included")}:
                      </label>
                      {formik.values.features.map((feature, index) => (
                        <div key={index} className="d-flex mb-2 align-items-center">
                          <div className="flex-grow-1">
                            <input
                              name={`features[${index}]`}
                              type="text"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.features[index]}
                              className="form-control"
                              placeholder={t("Enter feature")}
                            />
                          </div>
                          <div className="ms-2">
                            {index === 0 ? (
                              <button
                                type="button"
                                className="btn btn-success ml-2"
                                onClick={() => {
                                  formik.setFieldValue("features", [...formik.values.features, ""]);
                                }}
                              >
                                <i className="fa fa-plus"></i> {t("Add More")}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-danger ml-2"
                                onClick={() => {
                                  const newFeatures = [...formik.values.features];
                                  newFeatures.splice(index, 1);
                                  formik.setFieldValue("features", newFeatures);
                                }}
                              >
                                <i className="fa fa-minus"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div> */}


                    <div className="mt-5">
                      <button
                        className="btn btn-info mr-2"
                        type="submit"
                        disabled={submitted ? true : null}
                      >
                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                        {t("btn_submit")}
                      </button>
                      <button
                        className="btn ripple btn-secondary"
                        type="button"
                        disabled={submitted ? true : null}
                        onClick={() => navigate(-1)}
                      >
                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                        {t("btn_cancel")}
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default EditSubscriptionPlan;
